'use client';

import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Clock, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Pick } from '@/lib/supabase';

function formatOdds(odds: number): string {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function RecentResults() {
  const [gradedPicks, setGradedPicks] = useState<Pick[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadResults() {
      try {
        const { data, error } = await supabase
          .from('picks')
          .select('*')
          .neq('result', 'pending')
          .order('posted_at', { ascending: false })
          .limit(10);

        if (error) throw error;
        setGradedPicks(data || []);
      } catch {
        setGradedPicks([]);
      } finally {
        setLoading(false);
      }
    }
    loadResults();
  }, []);

  const last10Wins = gradedPicks.filter((p) => p.result === 'win').length;
  const last10Units = gradedPicks.reduce((a, p) => a + (p.units_result || 0), 0);

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Recent Results</h2>
        {!loading && gradedPicks.length > 0 && (
          <div className="flex items-center gap-4">
            <span className="text-xs text-[hsl(var(--foreground-muted))]">
              Last {gradedPicks.length}: <span className="text-[hsl(var(--foreground))] font-mono font-medium">{last10Wins}-{gradedPicks.length - last10Wins}</span>
            </span>
            <span className="text-xs text-[hsl(var(--foreground-muted))]">
              Units: <span className={`font-mono font-bold ${last10Units >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                {last10Units >= 0 ? '+' : ''}{last10Units.toFixed(2)}u
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="stat-card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2">
            <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
            <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading results...</span>
          </div>
        ) : gradedPicks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <p className="text-sm font-medium text-[hsl(var(--foreground))]">No graded picks yet</p>
            <p className="text-xs text-[hsl(var(--foreground-muted))]">Results will appear here once picks are graded.</p>
          </div>
        ) : (
          <div className="divide-y divide-[hsl(var(--border)/0.5)]">
            {gradedPicks.map((pick) => {
              const isWin = pick.result === 'win';
              const isLoss = pick.result === 'loss';

              return (
                <div
                  key={pick.id}
                  className="flex items-center gap-4 px-5 py-3.5 hover:bg-[hsl(var(--card-elevated))] transition-colors duration-100 group"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isWin ? 'bg-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.2)]'
                    : isLoss ? 'bg-red-500/10 border border-red-500/20' :'bg-yellow-500/10 border border-yellow-500/20'
                  }`}>
                    {isWin ? <TrendingUp size={14} className="text-[hsl(var(--primary))]" />
                    : isLoss ? <TrendingDown size={14} className="text-red-400" />
                    : <Minus size={14} className="text-yellow-400" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[hsl(var(--foreground))] truncate">{pick.pick}</span>
                      <span className="text-xs text-[hsl(var(--foreground-muted))] truncate">{pick.game}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-[hsl(var(--foreground-muted))]">{pick.sport}</span>
                      <span className="font-mono text-xs text-[hsl(var(--foreground-muted))]">{formatOdds(pick.odds)}</span>
                      <span className="text-xs text-[hsl(var(--foreground-muted))]">{pick.units}u</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--foreground-muted))] flex-shrink-0">
                    <Clock size={11} />
                    {formatDate(pick.posted_at)}
                  </div>

                  <div className="text-right flex-shrink-0 w-16">
                    <span className={`font-mono font-bold text-sm tabular-nums ${
                      isWin ? 'text-[hsl(var(--primary))]' : isLoss ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {pick.units_result !== null && pick.units_result !== undefined
                        ? `${pick.units_result >= 0 ? '+' : ''}${pick.units_result.toFixed(2)}u`
                        : '0.00u'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}